/* Jump About suburb selector. CLEAN REWRITE for the Option A architecture.
 * 2026-06-14. STAGED, NOT DEPLOYED, NOT YET VERIFIED on a live render.
 *
 * What changed vs the live v9 (delivery-suburb-selector.js), and why:
 *  - Cart write is now ONE GUARDED PASS: a single /cart/update.js (sets the
 *    delivery_suburb + delivery_fee attributes AND zeroes ALL 11 delivery
 *    variants) then a single /cart/add.js for the chosen fee variant. No
 *    /cart.js read, no /cart/change.js loop, no re-add storm. This is the fix
 *    for the 429 / Cloudflare cascade. Max 2 writes per pick, never a burst.
 *  - REMOVED gateAddButton / data-ja-suburb-locked: the silent dead-button
 *    class of bug. The mandatory-delivery gate is now SERVER-SIDE via a public
 *    Cart-and-Checkout Validation app (BoomGate), which is unbypassable and
 *    covers express checkout. The theme no longer gates anything client-side.
 *  - REMOVED the form submit interceptor (stopImmediatePropagation) that could
 *    swallow the theme/app add path.
 *  - REMOVED gateCartCheckout and the client-side PayPal/dynamic-checkout hiding
 *    (BoomGate handles checkout blocking server-side now).
 *  - REMOVED the cart-page reload after commit (its re-burst tripped 429).
 *  - Mount: see MOUNT_TARGET below. After IzyRent is removed and BookThatApp is
 *    installed, point MOUNT_TARGET at BTA's booking element so the suburb step
 *    renders ABOVE the date picker (date-first funnel). Until then it mounts
 *    into #ja-delivery-mount as before. THIS is the only post-teardown wiring.
 *
 * The widget is now purely UX: it shows the exact fee at the postcode step and
 * writes the cart attribute + fee line. It holds no gate.
 *
 * 2026-07-30, go-live loop row `cart-drawer-stale-after-suburb-pick`. The two
 * cart writes now carry `sections` + `sections_url`, so each response brings back
 * the rendered cart-icon-bubble, cart-drawer and main-cart HTML, and the widget
 * applies it. Still two writes per pick: bundled section rendering adds no
 * requests. Before this, the drawer and the header bubble stayed frozen after a
 * pick, and because /cart/add.js PREPENDS the fee line, the drawer's stale remove
 * buttons deleted the WRONG line (the delivery fee instead of the product).
 */
(function () {
  'use strict';

  const STORAGE_KEY = 'jumpabout_suburb';
  const ASSET_URL = window.JumpAboutSuburbsURL || '/cdn/shop/files/delivery-suburbs.json';
  const ATTR_SUBURB = 'delivery_suburb';   // BoomGate gates on the PRESENCE of this attribute
  const ATTR_FEE = 'delivery_fee';
  // All 11 live JA-DELIVERY variant IDs (store 16xsd9-0b, product 10050541093113),
  // verified via Admin API 2026-06-14. Used to blind-zero any stale delivery line
  // in ONE update call, so no cart read is needed.
  const DELIVERY_VARIANT_IDS = [
    48368985932025, // $60
    48369111597305, // $70
    48369111630073, // $80
    48369111662841, // $90
    48369111695609, // $100
    48369111728377, // $110
    48369111761145, // $120
    48369111793913, // $130
    48369111826681, // $140
    48369111859449, // $150
    48369111892217  // $160
  ];

  // BTA installed 2026-07-06. Its widget script sets `display:none !important`
  // inline on #ja-delivery-mount, which removed the suburb picker from every
  // product page. Fix (2026-07-07): relocate the mount directly ABOVE the
  // Check Dates & Book button row and pin it visible; a MutationObserver
  // re-clears the style if BTA re-applies it.
  const MOUNT_TARGET = { mountId: 'ja-delivery-mount', aboveSelector: '.product-form__buttons' };

  function pinVisible(mount){
    const force = () => {
      if (getComputedStyle(mount).display === 'none') {
        mount.style.setProperty('display', 'block', 'important');
      }
    };
    force();
    new MutationObserver(force).observe(mount, { attributes: true, attributeFilter: ['style'] });
  }

  let widgetSeq = 0;
  const isProductPage = () => /\/products\//.test(location.pathname) && !/\/products\/delivery-fee/.test(location.pathname);
  const isDeliveryPanel = () => !!document.querySelector('#ja-suburb-calculator-target');

  function debounce(fn, ms){ let t; return (...a)=>{clearTimeout(t); t=setTimeout(()=>fn(...a),ms);}; }
  function load(){ try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null'); } catch(e){ return null; } }
  function save(v){ if(v) localStorage.setItem(STORAGE_KEY, JSON.stringify(v)); else localStorage.removeItem(STORAGE_KEY); }
  function postJSON(url, body){ return fetch(url,{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify(body)}); }
  // Parse a cart-write response without ever throwing. A Cloudflare challenge or a
  // 4xx returns HTML, and a throw here would skip the ja:delivery-line-count
  // dispatch that ja-delivery-reconcile.js depends on.
  // NOTE, measured 2026-07-30, not assumed: the Ajax Cart API answers with
  // `content-type: text/javascript`, NOT application/json. A content-type sniff for
  // "json" rejects every valid response and silently disables the repaint. So the
  // only checks here are response.ok plus the parse itself.
  async function postCart(url, body){
    try {
      const r = await postJSON(url, body);
      if (!r || !r.ok) return null;
      return await r.json();
    } catch(e){ return null; }
  }

  // 2026-07-30, go-live loop row `cart-drawer-stale-after-suburb-pick`.
  // This widget used to write the cart with NO repaint, so the drawer and the
  // header count bubble stayed frozen at whatever the page load rendered. That is
  // not only cosmetic: the drawer's remove buttons carry the LINE INDICES from the
  // stale render and /cart/add.js PREPENDS, so after a pick every index shifts by
  // one and clicking Remove on the product removed the DELIVERY FEE instead.
  // The fix is the doctrine-correct one: ask for the rendered sections in the SAME
  // response as the write (bundled section rendering, zero extra requests), then
  // apply them. The map, the selectors and the is-empty handling below are copied
  // from the theme's own `cart:refresh` handler in assets/global.js so the widget
  // repaints exactly the way every other cart mutation on this store does. That
  // handler is the CHEAP FALLBACK, deliberately not used here: it costs one extra
  // request, and request hygiene is why this widget was rewritten once already.
  const REPAINT_SECTIONS = [
    { id: 'cart-icon-bubble', selector: '.cart-count-bubble' },
    { id: 'cart-drawer', selector: '#CartDrawer' },
    { id: 'main-cart', selector: '.main-cart' }
  ];
  const SECTION_IDS = REPAINT_SECTIONS.map(s => s.id).join(',');
  // `main-cart` only renders in the cart page's context, so the sections are
  // requested against /cart, same as the theme's own handler and as
  // ja-delivery-reconcile.js.
  const SECTIONS_URL = '/cart';

  function repaintCart(sections){
    if (!sections) return false;
    let applied = false;
    REPAINT_SECTIONS.forEach(section => {
      const html = sections[section.id];
      if (!html) return;
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const source = parsed.querySelector(section.selector);
      const destinations = document.querySelectorAll(section.selector);
      if (!source || !destinations.length) return;
      destinations.forEach(destination => {
        destination.innerHTML = source.innerHTML;
        if (source.classList.contains('is-empty')) destination.classList.add('is-empty');
        else destination.classList.remove('is-empty');
      });
      applied = true;
    });
    return applied;
  }

  // THE CLEAN CART WRITE. One update (attrs + zero all delivery variants) + one add.
  // No read, no change-loop, no reload. In-flight guard prevents overlapping picks.
  // 2026-07-22 (go-live loop, cart-function row). The guard below used to be
  // `if (inFlight) return;` which SILENTLY DISCARDED a suburb change made while
  // a previous write was still running. The widget still painted the new suburb
  // and fee, so the customer saw "Singleton, Delivery: $150" while the cart
  // carried the old fee, or none at all. Observed once during the cart-function
  // verification sweep (Newcastle picked, cart left with zero delivery lines and
  // no delivery_suburb attribute).
  // The fix keeps the 429 protection exactly as it was (never more than one
  // update+add pair in flight) but no longer loses the customer's choice: a pick
  // made during a write is parked in `pending`, coalescing to the LATEST pick,
  // and replayed as soon as the current write finishes.
  let inFlight = false;
  let pending;          // undefined = nothing queued; null = queued "clear"
  let hasPending = false;
  async function applySuburb(suburb){
    if (inFlight) { pending = suburb; hasPending = true; return; }
    inFlight = true;
    try {
      const updates = {};
      DELIVERY_VARIANT_IDS.forEach(id => { updates[id] = 0; });
      if (suburb) updates[Number(suburb.variant_id)] = 0; // ensure stale removed before re-add
      const attributes = suburb
        ? { [ATTR_SUBURB]: `${suburb.name} ${suburb.postcode}`, [ATTR_FEE]: `$${suburb.fee}` }
        : { [ATTR_SUBURB]: '', [ATTR_FEE]: '' };
      // Still exactly two writes per pick, one per endpoint, never a burst. The
      // `sections` params add no requests: they only change what each response
      // carries back. The LAST write is the authoritative state, so a pick is
      // repainted from the add response and a clear from the update response.
      let state = await postCart('/cart/update.js', {
        updates, attributes, sections: SECTION_IDS, sections_url: SECTIONS_URL
      });
      if (suburb) {
        const added = await postCart('/cart/add.js', {
          id: Number(suburb.variant_id), quantity: 1,
          sections: SECTION_IDS, sections_url: SECTIONS_URL
        });
        if (added && added.sections) state = added;
      }
      repaintCart(state && state.sections);
      // 2026-07-28, go-live loop row `cart-orphan-delivery-fee`. This widget
      // writes the cart with no theme repaint and no theme event, so nothing
      // else on the page knows the cart changed. Publish the one fact the write
      // guarantees, on a JumpAbout-private channel: after a pick there is
      // exactly one delivery line, after a clear there are none. Costs no
      // request and no cart read. Deliberately NOT the theme's `cart:change`,
      // which would wake app listeners for a write they did not make, and
      // deliberately not a payload the caller would have to trust: /cart/add.js
      // returns the line item, not the cart.
      if (window.JACartTally) {
        window.JACartTally.delivery = suburb ? 1 : 0;
      }
      document.dispatchEvent(new CustomEvent('ja:delivery-line-count', {
        detail: { delivery: suburb ? 1 : 0 }
      }));
      // Immediate gate flip so Proceed is re-enabled as soon as the fee lands
      // (without waiting for a theme cart:change that this write does not fire).
      try {
        if (suburb) setSuburbGate(false);
        else if (window.JACartTally) setSuburbGate(tallyNeedsSuburb(window.JACartTally));
      } catch (gateErr) { /* gate functions hoist after init; first-paint races only */ }
    } catch (e) {
      console.warn('[JumpAbout] delivery cart write failed:', e);
    } finally {
      inFlight = false;
      if (hasPending) {
        const next = pending;
        hasPending = false; pending = undefined;
        applySuburb(next);
      }
    }
  }

  function buildUI(){
    const seq = ++widgetSeq;
    const listId = `ja-suburb-results-${seq}`, labelId = `ja-suburb-label-${seq}`;
    const c = document.createElement('div');
    c.className = 'ja-suburb-widget';
    c.innerHTML = `
      <div class="ja-suburb-widget__label" id="${labelId}">Where are we delivering to?</div>
      <div class="ja-suburb-widget__input-wrap" data-pick>
        <input type="text" class="ja-suburb-widget__input" placeholder="Type your suburb or postcode" autocomplete="off"
          role="combobox" aria-autocomplete="list" aria-expanded="false" aria-haspopup="listbox"
          aria-controls="${listId}" aria-labelledby="${labelId}">
        <div class="ja-suburb-widget__results" id="${listId}" hidden role="listbox" aria-label="Suburb results"></div>
      </div>
      <div class="ja-suburb-widget__selected" hidden>
        <div class="ja-suburb-widget__selected-info">
          <span class="ja-suburb-widget__selected-name"></span>
          <span class="ja-suburb-widget__selected-fee"></span>
        </div>
        <button type="button" class="ja-suburb-widget__change">Change suburb</button>
      </div>
      <div class="ja-suburb-widget__hint">Pick your suburb to see your exact delivery fee. Setup and pack down are included in the hire price.</div>`;
    return c;
  }

  function showSelected(w, s){
    w.querySelector('[data-pick]').style.display = 'none';
    const sel = w.querySelector('.ja-suburb-widget__selected');
    sel.querySelector('.ja-suburb-widget__selected-name').textContent = `${s.name} (${s.postcode})`;
    sel.querySelector('.ja-suburb-widget__selected-fee').textContent = `Delivery: $${s.fee}`;
    sel.hidden = false;
  }
  function showSearch(w){
    const sel = w.querySelector('.ja-suburb-widget__selected');
    const input = w.querySelector('.ja-suburb-widget__input');
    sel.hidden = true; w.querySelector('[data-pick]').style.display = '';
    input.value = ''; w.querySelector('.ja-suburb-widget__results').hidden = true;
    input.setAttribute('aria-expanded','false'); input.removeAttribute('aria-activedescendant'); input.focus();
  }
  function closePopup(w){
    const r = w.querySelector('.ja-suburb-widget__results'), i = w.querySelector('.ja-suburb-widget__input');
    r.hidden = true; i.setAttribute('aria-expanded','false'); i.removeAttribute('aria-activedescendant');
  }
  function search(suburbs, q){
    const Q = q.trim().toLowerCase();
    if (!Q || Q.length < 2) return [];
    return suburbs.filter(s => s.name.toLowerCase().includes(Q) || (s.postcode||'').includes(Q)).slice(0,8);
  }
  function renderResults(w, matches, state){
    const r = w.querySelector('.ja-suburb-widget__results'), i = w.querySelector('.ja-suburb-widget__input');
    state.activeIndex = -1; i.removeAttribute('aria-activedescendant');
    if (!matches.length){
      r.innerHTML = `<div class="ja-suburb-widget__no-match">No match. <a href="/pages/contact">Contact us</a> for a custom delivery quote.</div>`;
      r.hidden = false; i.setAttribute('aria-expanded','true'); return;
    }
    const listId = r.id;
    r.innerHTML = matches.map((s,idx)=>`
      <div class="ja-suburb-widget__option" role="option" id="${listId}-opt-${idx}" aria-selected="false"
        data-name="${s.name}" data-postcode="${s.postcode}" data-fee="${s.fee}" data-variant-id="${s.variant_id}">
        <span class="ja-suburb-widget__option-name">${s.name}</span>
        <span class="ja-suburb-widget__option-postcode">${s.postcode}</span>
        <span class="ja-suburb-widget__option-fee">$${s.fee}</span>
      </div>`).join('');
    r.hidden = false; i.setAttribute('aria-expanded','true');
  }
  function setActive(w, state, index){
    const opts = w.querySelectorAll('.ja-suburb-widget__option'), i = w.querySelector('.ja-suburb-widget__input');
    if(!opts.length){ state.activeIndex=-1; i.removeAttribute('aria-activedescendant'); return; }
    if(index<0) index=opts.length-1; if(index>=opts.length) index=0;
    opts.forEach((o,idx)=>o.setAttribute('aria-selected', idx===index?'true':'false'));
    state.activeIndex=index; i.setAttribute('aria-activedescendant', opts[index].id); opts[index].scrollIntoView({block:'nearest'});
  }
  function suburbFromOpt(o){ return { name:o.dataset.name, postcode:o.dataset.postcode, fee:parseInt(o.dataset.fee,10), variant_id:o.dataset.variantId }; }

  function commit(w, s){
    save(s); showSelected(w, s); closePopup(w);
    applySuburb(s); // clean async write, not awaited so UI is instant
  }

  function wire(w, suburbs){
    const input = w.querySelector('.ja-suburb-widget__input');
    const results = w.querySelector('.ja-suburb-widget__results');
    const change = w.querySelector('.ja-suburb-widget__change');
    const state = { activeIndex: -1 };
    const doSearch = () => { if(!input.value){ closePopup(w); return; } renderResults(w, search(suburbs, input.value), state); };
    const handle = debounce(doSearch, 100);
    input.addEventListener('input', handle);
    input.addEventListener('focus', () => { if(input.value) doSearch(); });
    input.addEventListener('keydown', (ev) => {
      const open = !results.hidden;
      switch(ev.key){
        case 'ArrowDown': ev.preventDefault(); if(!open) doSearch(); setActive(w,state,state.activeIndex+1); break;
        case 'ArrowUp': ev.preventDefault(); if(!open){ doSearch(); setActive(w,state,-1);} else setActive(w,state,state.activeIndex-1); break;
        case 'Enter': ev.preventDefault(); if(open && state.activeIndex>=0){ const c=results.querySelectorAll('.ja-suburb-widget__option'); if(c[state.activeIndex]) commit(w, suburbFromOpt(c[state.activeIndex])); } break;
        case 'Escape': if(open){ ev.preventDefault(); closePopup(w);} break;
      }
    });
    results.addEventListener('pointerdown', (ev) => {
      if(ev.pointerType!=='mouse'||ev.button!==0) return;
      const o = ev.target.closest('.ja-suburb-widget__option'); if(!o) return;
      ev.preventDefault(); commit(w, suburbFromOpt(o));
    }, { capture:true });
    results.addEventListener('click', (ev) => {
      if(results.hidden) return;
      const o = ev.target.closest('.ja-suburb-widget__option'); if(!o) return;
      commit(w, suburbFromOpt(o));
    });
    document.addEventListener('click', (ev)=>{ if(w.isConnected && !w.contains(ev.target)) closePopup(w); });
    change.addEventListener('click', () => { save(null); state.activeIndex=-1; showSearch(w); applySuburb(null); });
  }

  async function loadSuburbs(){
    try { const r = await fetch(ASSET_URL); return await r.json(); }
    catch(e){ console.warn('[JumpAbout] suburb data load failed:', e); return null; }
  }

  function mountInto(target, suburbs, initial){
    if (target.querySelector('.ja-suburb-widget')) return;
    const w = buildUI(); target.appendChild(w); wire(w, suburbs);
    if (initial) showSelected(w, initial);
  }

  async function init(){
    if (!isProductPage()) return;
    const suburbs = await loadSuburbs(); if (!suburbs) return;
    const initial = load();
    const mount = document.getElementById(MOUNT_TARGET.mountId);
    if (!mount) { requestAnimationFrame(init); return; }
    // Relocate the mount directly above the Check Dates & Book button row so
    // the suburb step reads before the booking step, and pin it visible
    // (BTA's widget script force-hides the mount with an inline style).
    if (MOUNT_TARGET.aboveSelector) {
      const anchor = document.querySelector(MOUNT_TARGET.aboveSelector);
      if (anchor && anchor.parentNode) anchor.parentNode.insertBefore(mount, anchor);
    }
    pinVisible(mount);
    mountInto(mount, suburbs, initial);
  }

  async function initPanel(){
    if (!isDeliveryPanel()) return;
    const target = document.querySelector('#ja-suburb-calculator-target');
    const suburbs = await loadSuburbs(); if (!suburbs) return;
    mountInto(target, suburbs, load());
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => { init(); initPanel(); });
  } else { init(); initPanel(); }

  /* ========================================================================
   * JA-2026-07-31 post-live: theme-side suburb gate.
   * BoomGate was meant to own this server-side, but live carts still reach
   * Pay with no suburb (measured run 44: attributes {}, Proceed enabled).
   * Three payment routes on cart: name=checkout, Shop Pay, PayPal express.
   * Gate is PRESENTATION-ONLY (class + disabled attr). Never intercept form
   * submit (that class of bug broke add-to-cart on this store).
   * ======================================================================== */
  const GATE_CLASS = 'ja-suburb-missing';

  // Live gate state. Seeded from the server-rendered tally at load, then kept
  // current ONLY by real cart data. window.JACartTally is a page-load snapshot
  // and is stale the moment anything is added, so it is never recomputed from
  // after boot; the open-observers below re-apply THIS value instead.
  let gateMissing = false;

  function cartNeedsSuburb(cart) {
    if (!cart || !Array.isArray(cart.items) || cart.items.length === 0) return false;
    let hasProduct = false;
    let hasDeliveryLine = false;
    for (let i = 0; i < cart.items.length; i++) {
      const sku = (cart.items[i].sku || '');
      if (sku.indexOf('JA-DELIVERY') === -1) hasProduct = true;
      else hasDeliveryLine = true;
    }
    if (!hasProduct) return false;
    // JA-2026-08-02 run 46. The ATTRIBUTE alone is not sufficient. Removing the
    // delivery line leaves delivery_suburb and delivery_fee set, which left the
    // gate open on a castle with no fee (measured live: $190, attributes still
    // 'Terrigal 2260', Proceed enabled). The fee LINE is what charges, so the
    // line is the test and the attribute is required alongside it.
    if (!hasDeliveryLine) return true;
    const suburb = cart.attributes && cart.attributes[ATTR_SUBURB];
    return !(suburb && String(suburb).trim());
  }

  function tallyNeedsSuburb(t) {
    if (!t) return false;
    return (t.products || 0) > 0 && (t.delivery || 0) === 0;
  }

  // Every control that can start a payment, across all routes measured on this
  // store 2026-08-02. Native submits take the disabled attribute; anchors cannot,
  // so they take aria-disabled and leave the tab order. CSS blocks the pointer on
  // both, and the CSS lives on <html> so it survives every section repaint.
  const GATE_SUBMITS = '.main-cart__buttons button[name="checkout"], '
    + '.cart-drawer__buttons button[name="checkout"], #CartDrawer-Checkout';
  const GATE_ANCHORS = '.ja-cart-notification-actions__checkout, '
    + 'cart-notification a[href*="/checkout"], #CartNotification a[href*="/checkout"]';

  function setSuburbGate(missing) {
    gateMissing = !!missing;
    document.documentElement.classList.toggle(GATE_CLASS, gateMissing);

    document.querySelectorAll(GATE_SUBMITS).forEach(function (btn) {
      if (gateMissing) {
        btn.setAttribute('disabled', 'disabled');
        btn.setAttribute('aria-disabled', 'true');
      } else if (!document.querySelector('.main-cart.is-empty, cart-drawer.is-empty')) {
        // Only re-enable if the cart is not empty (theme may have disabled it).
        btn.removeAttribute('disabled');
        btn.removeAttribute('aria-disabled');
      }
    });

    document.querySelectorAll(GATE_ANCHORS).forEach(function (a) {
      if (gateMissing) {
        a.setAttribute('aria-disabled', 'true');
        a.setAttribute('tabindex', '-1');
      } else {
        a.removeAttribute('aria-disabled');
        a.removeAttribute('tabindex');
      }
    });
  }

  function refreshSuburbGateFromCart(cart) {
    setSuburbGate(cartNeedsSuburb(cart));
  }

  // Re-apply the KNOWN state to DOM that has just been repainted. Never recompute
  // here: the only cheap source at this moment is the stale page-load tally, and
  // recomputing from it is precisely the bug this replaced.
  function reapplySuburbGate() {
    setSuburbGate(gateMissing);
  }

  function bootSuburbGate() {
    if (window.JACartTally) setSuburbGate(tallyNeedsSuburb(window.JACartTally));

    document.addEventListener('cart:change', function (ev) {
      const cart = ev && ev.detail && (ev.detail.cart || ev.detail);
      if (cart && cart.items) refreshSuburbGateFromCart(cart);
      else reapplySuburbGate();
    });

    document.addEventListener('ja:delivery-line-count', function (ev) {
      const d = ev && ev.detail;
      if (!d) return;
      // The widget just wrote the cart. A delivery line now exists, so the gate
      // lifts; if it went to zero the cart is back to product-without-fee.
      setSuburbGate(!(d.delivery > 0));
    });

    // Re-apply after the drawer and the post-add notification repaint their
    // buttons from section HTML. Scoped to one element and one attribute each.
    const drawer = document.getElementById('CartDrawer');
    if (drawer) {
      new MutationObserver(reapplySuburbGate)
        .observe(drawer, { attributes: true, attributeFilter: ['open', 'class'] });
    }
    const notification = document.getElementById('CartNotification');
    if (notification) {
      new MutationObserver(reapplySuburbGate)
        .observe(notification, { attributes: true, attributeFilter: ['class', 'open'] });
      // Its inner markup is replaced wholesale by the add response, which does not
      // touch the host's attributes, so watch the children too.
      new MutationObserver(reapplySuburbGate)
        .observe(notification, { childList: true, subtree: true });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootSuburbGate);
  } else {
    bootSuburbGate();
  }
})();
